import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import BookedOfferDetailsCTA from './BookedOfferDetailsCTA'
import LEOfferCTAs from './LEOfferCTAs'

import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import ViewNearbyExperiencesCta from 'tripPlanner/components/Buttons/ViewNearbyExperiencesCta'
import type { DetailItemCardVariant } from 'tripPlanner/components/DetailItemCard/common/types'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import TripContext from 'tripPlanner/contexts/TripContext'
import type { ExperienceItem } from 'tripPlanner/types/tripItem'
import { isLeBookmark, countTravellers } from 'tripPlanner/utils'
import { addTripIdParam } from 'tripPlanner/utils/url'
import { mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { getIsLuxPlusExclusiveAccess } from 'tripPlanner/utils/luxPlux'
import useExperience from 'hooks/Experiences/useExperience'
import ModalContext from 'contexts/ModalContext'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import type { TransferBookingModalResolve } from 'components/Experiences/ExperienceTransfers/ExperienceTransferBookingModal'
import ExperienceTransferBookingModal from 'components/Experiences/ExperienceTransfers/ExperienceTransferBookingModal'
import { useAppDispatch } from 'hooks/reduxHooks'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generateTransferCheckoutItem } from 'lib/checkout/experiences/cart'
import ExperienceTripPlannerModal from './ExperienceTripPlannerModal'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'

const TO_HOTEL: App.ExperienceTransferType = 'AIRPORT-TO-HOTEL'
const TO_AIRPORT: App.ExperienceTransferType = 'HOTEL-TO-AIRPORT'

interface Props {
  item: ExperienceItem
  isOfferAvailable: boolean
  variant: DetailItemCardVariant
  offerLink?: string
  onViewOffer?: () => void
}

function ExperienceCtaContent(props: Props) {
  const intl = useIntl()
  const { isOfferAvailable, item, item: { tripId }, offerLink, onViewOffer } = props

  const [experience, experienceLoading] = useExperience(item.savedItemData?.experienceId)
  const { data: trip } = useTrip({ tripId })
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()

  const { viewOfferCopy } = useTripPlannerCTALabels()
  const tripContext = useContext(TripContext)
  const isPublicTrip = tripContext?.isPublicTrip
  const canEdit = useCanEditTrip()
  const isLuxPlusExclusiveAccess = getIsLuxPlusExclusiveAccess(item)
  const tripStartDate = tripContext?.startDate?.format(ISO_DATE_FORMAT)
  const tripEndDate = tripContext?.endDate?.format(ISO_DATE_FORMAT)
  const tripTravellers = trip?.travellerRooms
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const travellersDefault = useMemo<App.ExperienceTransferTravellers>(() => {
    if (!tripTravellers || tripTravellers.length === 0) {
      return { adults: 2, children: 0, childSeats: 0 }
    }
    const { adults, children } = countTravellers(tripTravellers)
    return { adults, children, childSeats: 0 }
  }, [tripTravellers])

  const onClickBuyExperienceButton = useCallback(async() => {
    if (!experience) return

    const experienceView = mapExperienceToBookingView(experience)
    const propertyLocation = {
      latitude: experience.location.latitude,
      longitude: experience.location.longitude,
    }

    // Ensure immersive trip context is set so checkout/confirmation know the current trip id
    setImmersiveMode({ tripId })

    if (experienceView.features?.transfer) {
      const createDefaultTransferView = (
        transferType: App.ExperienceTransferType,
      ): App.ExperienceTransferView => ({
        type: transferType,
        date: item.startDate?.format(ISO_DATE_FORMAT),
        hotel: {},
        travellers: travellersDefault,
        baggage: {
          bags: travellersDefault.adults + (travellersDefault.children ?? 0),
          oversizedBags: 0,
        },
      })

      const defaultToHotelTransfer = createDefaultTransferView(TO_HOTEL)
      const defaultToAirportTransfer = createDefaultTransferView(TO_AIRPORT)
      const result = await showModal<TransferBookingModalResolve>(
        <ExperienceTransferBookingModal
          experience={experienceView}
          defaultToHotelTransfer={defaultToHotelTransfer}
          defaultToAirportTransfer={defaultToAirportTransfer}
        />,
      )

      const transfers = result?.transfers
      if (transfers && transfers.length > 0) {
        const items = transfers.map(transfer => generateTransferCheckoutItem(experienceView.originalExperience, transfer))
        dispatch(initialiseCheckout(items))
        dispatch(navigateToCheckout())
      }
      return
    } else {
      return showModal(
        <ExperienceTripPlannerModal
          experienceView={experienceView}
          savedItemData={item.savedItemData}
          tripStartDate={tripStartDate}
          tripEndDate={tripEndDate}
          propertyLocation={propertyLocation}
          isLuxPlusEnabled={isLuxPlusExclusiveAccess}
        />,
      )
    }
  }, [experience, isLuxPlusExclusiveAccess, showModal, item.savedItemData, item.startDate, tripStartDate, tripEndDate, setImmersiveMode, tripId, dispatch, travellersDefault])

  if (isPublicTrip) {
    return (
      <Group direction="horizontal" gap={12}>
        <SaveTripItemButton item={item} />
        {offerLink && (
          <TextButton kind="primary" to={offerLink} target="_blank" onClick={onViewOffer}>
            {viewOfferCopy}
          </TextButton>
        )}
      </Group>
    )
  }

  if (item.isBooked && item.confirmationCode) {
    return <BookedOfferDetailsCTA item={item} />
  }

  if (!isOfferAvailable) {
    const hasPlannedInfo = !!item.startDate

    return (
      <Group
        direction="horizontal"
        gap={8}
        tabletHorizontalAlign="space-between"
        horizontalAlign="stretch"
      >
        {hasPlannedInfo && offerLink && (
          <TextButton
            kind="tertiary"
            to={addTripIdParam(offerLink, tripId, canEdit)}
            target="_blank"
            onClick={onViewOffer}
          >
            {intl.formatMessage({
              id: 'eet6dCi3AjmMbZ0JnouD',
              defaultMessage: 'Change details',
              description: 'Button to change booking details for an experience trip item',
            })}
          </TextButton>
        )}
        {item.place.type === 'GEO' && (
          <ViewNearbyExperiencesCta
            lat={item.place.point.lat}
            lng={item.place.point.lng}
            tripId={item.tripId}
            showSearch={props.variant === 'tripSummary'}
          />
        )}
      </Group>
    )
  }

  if (!item.isBooked && canEdit) {
    return (
      <ProductPaletteProvider
        palette={isLuxPlusExclusiveAccess ? 'lux-plus' : 'default'}
      >
        <Group
          direction="horizontal"
          verticalAlign="center"
          horizontalAlign="stretch"
          tabletHorizontalAlign="space-between"
          gap={12}
        >
          <TextButton
            kind="primary"
            disabled={experienceLoading}
            onClick={onClickBuyExperienceButton}
          >
            {viewOfferCopy}
          </TextButton>
        </Group>
      </ProductPaletteProvider>
    )
  }

  if (isLeBookmark(item) && offerLink) {
    return <LEOfferCTAs item={item} variant={props.variant} offerLink={offerLink} />
  }

  return null
}

export default ExperienceCtaContent
